import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { ConvexHttpClient } from "convex/browser";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { handleCartError, formatCartErrorMessage } from "@/lib/utils/cart-errors";
import { toast } from "@/lib/toast";
import { trackAddToCart } from "@/lib/analytics/gtag";

// Initialize Convex client for mutations
const convexClient = new ConvexHttpClient(
  process.env.NEXT_PUBLIC_CONVEX_URL!
);

export interface CartItem {
  _id: Id<"cartItems">;
  productId: Id<"products">;
  productName: string;
  productSlug: string;
  productImage: string;
  brandId: Id<"brands">;
  brandName: string;
  variantId?: string;
  selectedVariants?: {
    size?: string;
    color?: string;
    isMulticolor?: boolean;
    multicolorList?: string[];
    [key: string]: any;
  };
  price: number;
  salePrice?: number;
  quantity: number;
  /**
   * The paid add-ons ticked on the product page: how many with EACH item and
   * the price when added (lib/config/websiteAddOns.ts). The order re-prices them.
   */
  addOns?: { id: string; name: string; qty: number; price: number }[];
  userId?: Id<"users">;
  sessionId?: string;
  expiresAt?: number;
  updatedAt: number;
  _creationTime: number;
}

export interface AppliedPromo {
  code: string;
  discount: number; // Calculated discount amount in UGX
  discountValue: number; // Original percentage or fixed value
  type: "percentage" | "fixed";
  promotionId?: string;
  isPersonalized?: boolean;
  targetCustomerPhone?: string;
}

interface CartStore {
  // Removed items array - now using Convex queries as source of truth
  isOpen: boolean;
  // Customer-facing "Order on WhatsApp" dialog visibility — opened from
  // the CartDrawer, closed after successful submission or cancel.
  whatsappCheckoutOpen: boolean;
  appliedPromo: AppliedPromo | null;
  currentBrandId: string | null;
  sessionId: string | null;

  // Cart operations (now call Convex mutations)
  addItem: (
    productId: Id<"products">,
    brandId: Id<"brands">,
    quantity: number,
    variantId?: string,
    selectedVariants?: any
  ) => Promise<void>;
  removeItem: (cartItemId: Id<"cartItems">) => Promise<void>;
  updateQuantity: (cartItemId: Id<"cartItems">, quantity: number) => Promise<void>;
  clearCart: (brandId?: Id<"brands">) => Promise<void>;
  mergeGuestCart: () => Promise<void>;

  // UI operations
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  openWhatsAppCheckout: () => void;
  closeWhatsAppCheckout: () => void;
  
  // Promo code methods
  setAppliedPromo: (promo: AppliedPromo | null) => void;
  removePromo: () => void;
  getDiscountAmount: () => number;
  
  // Brand scoping methods
  setBrandContext: (brandId: string | null) => void;
  
  // Session management
  initializeSession: () => void;
  clearSessionForAuthenticatedUser: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      isOpen: false,
      whatsappCheckoutOpen: false,
      appliedPromo: null,
      currentBrandId: null,
      sessionId: null,

      // Initialize session ID for guest users
      initializeSession: () => {
        const existingSessionId = get().sessionId;
        if (!existingSessionId) {
          // Generate new session ID: "guest-" + UUID v4
          const uuid = crypto.randomUUID();
          const newSessionId = `guest-${uuid}`;
          console.log("[CartStore] Initializing session:", newSessionId);
          set({ sessionId: newSessionId });
        }
      },

      // Add item to cart (calls Convex mutation)
      addItem: async (productId, brandId, quantity, variantId, selectedVariants) => {
        try {
          const sessionId = get().sessionId;
          
          console.log("[CartStore] Adding item to cart:", {
            productId,
            brandId,
            quantity,
            sessionId,
            variantId,
            selectedVariants,
          });

          // Call Convex mutation - no optimistic updates
          const cartItem: CartItem = await (convexClient.mutation as any)(
            api.cart.core.addToCart,
            {
              productId,
              brandId,
              quantity,
              variantId,
              selectedVariants,
              sessionId: sessionId || undefined,
            }
          );

          console.log("[CartStore] Item added successfully:", {
            cartItemId: cartItem._id,
            productId: cartItem.productId,
            brandId: cartItem.brandId,
            sessionId: cartItem.sessionId,
            quantity: cartItem.quantity,
          });

          // Analytics: report add_to_cart here rather than in a component, so
          // EVERY add path on the storefront is covered (product page, quick
          // add from a card, related products). Never let a tracking failure
          // break adding to a cart.
          try {
            const anyItem = cartItem as any;
            trackAddToCart({
              id: String(cartItem.productId),
              name: anyItem.productName || anyItem.name || "Product",
              category: anyItem.categoryName,
              price: Number(anyItem.price ?? anyItem.unitPrice ?? 0),
              quantity: Number(cartItem.quantity) || 1,
            });
          } catch (analyticsError) {
            console.warn("[CartStore] add_to_cart tracking skipped:", analyticsError);
          }
        } catch (error) {
          console.error("[CartStore] Failed to add item:", error);
          
          // Handle error with utilities
          const cartError = handleCartError("addItem", error, {
            productId,
            quantity,
            variantId,
          });
          
          // Re-throw with formatted message for component to handle
          throw new Error(formatCartErrorMessage(cartError));
        }
      },

      // Remove item from cart (calls Convex mutation)
      removeItem: async (cartItemId) => {
        try {
          const sessionId = get().sessionId;

          // Call Convex mutation - no optimistic updates
          await convexClient.mutation(api.cart.core.removeCartItem, {
            cartItemId,
            sessionId: sessionId || undefined,
          });
        } catch (error) {
          // Handle error with utilities
          const cartError = handleCartError("removeItem", error, {
            productId: cartItemId,
          });
          
          throw new Error(formatCartErrorMessage(cartError));
        }
      },

      // Update item quantity (calls Convex mutation)
      updateQuantity: async (cartItemId, quantity) => {
        try {
          const sessionId = get().sessionId;

          // Call Convex mutation - no optimistic updates
          await convexClient.mutation(
            api.cart.core.updateCartItemQuantity,
            {
              cartItemId,
              quantity,
              sessionId: sessionId || undefined,
            }
          );
        } catch (error) {
          // Handle error with utilities
          const cartError = handleCartError("updateQuantity", error, {
            productId: cartItemId,
            quantity,
          });
          
          throw new Error(formatCartErrorMessage(cartError));
        }
      },

      // Clear cart for a brand (calls Convex mutation)
      clearCart: async (brandId) => {
        try {
          const sessionId = get().sessionId;
          const targetBrandId = brandId || (get().currentBrandId as Id<"brands">);
          
          if (!targetBrandId) {
            throw new Error("Brand ID is required to clear cart");
          }

          // Call Convex mutation - no optimistic updates
          await convexClient.mutation(api.cart.core.clearCart, {
            brandId: targetBrandId,
            sessionId: sessionId || undefined,
          });
          
          // Clear promo when cart is cleared
          set({ appliedPromo: null });
        } catch (error) {
          // Handle error with utilities
          const cartError = handleCartError("clearCart", error, {
            productId: brandId,
          });
          
          throw new Error(formatCartErrorMessage(cartError));
        }
      },

      // Merge guest cart with authenticated user cart (calls Convex mutation)
      mergeGuestCart: async () => {
        try {
          const sessionId = get().sessionId;
          
          if (!sessionId) {
            // No guest cart to merge
            return;
          }

          // Call Convex mutation
          await convexClient.mutation(
            api.cart.core.mergeGuestCart,
            {
              sessionId,
            }
          );

          // Clear session ID after merge
          set({ sessionId: null });
        } catch (error) {
          console.error("Failed to merge guest cart:", error);
          throw error;
        }
      },

      // UI operations
      openCart: () => {
        set({ isOpen: true });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      toggleCart: () => {
        set({ isOpen: !get().isOpen });
      },

      // WhatsApp checkout dialog — opening it implicitly closes the cart
      // drawer so the dialog has the full focus.
      openWhatsAppCheckout: () => {
        set({ whatsappCheckoutOpen: true, isOpen: false });
      },

      closeWhatsAppCheckout: () => {
        set({ whatsappCheckoutOpen: false });
      },

      // Promo code methods
      setAppliedPromo: (promo) => {
        set({ appliedPromo: promo });
      },

      removePromo: () => {
        set({ appliedPromo: null });
      },

      getDiscountAmount: () => {
        const promo = get().appliedPromo;
        if (!promo) return 0;
        return promo.discount;
      },

      // Brand scoping methods
      setBrandContext: (brandId) => {
        const currentBrand = get().currentBrandId;
        // If switching brands, clear promo (promos are brand-specific)
        if (currentBrand !== brandId) {
          set({ currentBrandId: brandId, appliedPromo: null });
        } else {
          set({ currentBrandId: brandId });
        }
      },

      // Clear sessionId for authenticated users (migration fix)
      clearSessionForAuthenticatedUser: () => {
        const currentSessionId = get().sessionId;
        if (currentSessionId) {
          console.log("[CartStore] Clearing sessionId for authenticated user:", currentSessionId);
          set({ sessionId: null });
        }
      },
    }),
    {
      name: "cart-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist sessionId and UI state
        // Cart items come from Convex queries
        sessionId: state.sessionId,
        appliedPromo: state.appliedPromo,
        currentBrandId: state.currentBrandId,
      }),
    }
  )
);
